import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, Globe2Icon, MapPinIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const COMPANIES = [{
  name: 'GSK',
  logo: 'https://cdn.magicpatterns.com/uploads/aCqnqXncNjk1xsdUQg5TFB/Screenshot_2026-08-20_at_21.22.24.png'
}, {
  name: 'LEO Pharma',
  logo: 'https://cdn.magicpatterns.com/uploads/qe6bAu8fK165LTd9pa9Tmm/Screenshot_2026-08-20_at_21.22.35.png'
}, {
  name: '3M and Solventum',
  logo: 'https://cdn.magicpatterns.com/uploads/8a94AChcQKnfAzckma8QZD/Screenshot_2026-08-27_at_12.40.13.png'
}, {
  name: 'AbbVie',
  logo: 'https://cdn.magicpatterns.com/uploads/pKJuSjA5sXAAftL8qfcZvo/Screenshot_2026-08-27_at_12.39.43.png'
}, {
  name: 'Healthcare company',
  logo: 'https://cdn.magicpatterns.com/uploads/aQbK55PqMKmQ6yF9dTcsFH/Screenshot_2026-08-20_at_21.22.20.png'
}] as const;
const REGIONS = ['Europe', 'Gulf', 'Africa', 'Asia', 'South America'] as const;
const MARKETS = ['Finland', 'Denmark', 'Germany', 'Poland', 'France', 'Netherlands', 'United Kingdom', 'Spain', 'Italy', 'Gulf', 'Morocco', 'Ivory Coast', 'Nigeria', 'South Africa', 'Kenya', 'Singapore', 'China', 'Peru', 'Brazil', 'Ireland', 'Sweden', 'Romania', 'Hungary'] as const;
export function SlideOperatingScale({}: SlideProps) {
  return <InteriorSlide surface data-id="element-522" data-mp="1t6rsweo109m">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.5rem,2.4vw,2.5rem)] flex-shrink-0" data-id="element-523" data-mp="1uhv6v478607">
        <p className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-524" data-mp="avoax1z0xcse">
          Global delivery experience
        </p>
        <h2 className="max-w-6xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-525" data-mp="1m8idu2x6et0b">
          Less than a year.{' '}
          <span className="text-[var(--brand)]" data-id="element-526" data-mp="ewrftdpnpid9">Already at scale.</span>
        </h2>
        <p className="mt-4 text-[clamp(1.1rem,1.9vw,1.85rem)] font-bold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-527" data-mp="eyvjbujn8fd0">
          New technology. Decades of know-how.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-528" data-mp="ot48c89gtq6f">
        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-black/5 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-4" data-id="element-529" data-mp="svr1qdlsbj5q">
          <div className="flex items-start justify-between gap-4" data-id="element-530" data-mp="9xo9lpxxx63p">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-531" data-mp="9yzb1j3wy1rn">
              <Building2Icon className="h-5 w-5" aria-hidden="true" data-id="element-532" data-mp="3dmrjypc0uap0" />
            </div>
            <span className="text-[clamp(2.5rem,4vw,4rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-533" data-mp="3dkomxlcgbhas">
              5
            </span>
          </div>
          <h3 className="mt-4 text-xl font-bold text-[var(--fg)]" data-id="element-534" data-mp="3158bwbil0t9l">
            Companies with managed campaigns
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]" data-id="element-535" data-mp="2x0qycu2tfc79">
            Selected organizations supported through continued campaign delivery.
          </p>
          <div className="mt-5 grid flex-1 grid-cols-2 gap-3" data-id="element-536" data-mp="2n5foap2cqt5u">
            {COMPANIES.map((company, index) => <figure key={company.name} className={`flex min-h-[4rem] items-center justify-center overflow-hidden rounded-2xl border border-[var(--brand)]/10 bg-white px-4 py-2 ${index === COMPANIES.length - 1 ? 'col-span-2' : ''}`} data-id="element-537" data-mp="2icmkc3epjout" data-mpi={index}>
                <img src={company.logo} alt={`${company.name} logo`} className="h-full max-h-14 w-full object-contain" data-id="element-538" data-mp="29awmaeqblro2" />
              </figure>)}
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.55
      }} className="relative flex min-h-0 flex-col justify-between overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.4rem,2.2vw,2.25rem)] text-white shadow-lg lg:col-span-3" data-id="element-539" data-mp="3cq4y11425ae">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full border-[28px] border-white/10" data-id="element-540" data-mp="3gqtikw0q8rfp" />
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-541" data-mp="3ksoibw30ewrd">
            <div className="h-6 w-6" aria-hidden="true" data-id="element-542" data-mp="36sxbqgetoine" />
          </div>
          <div className="relative" data-id="element-543" data-mp="3brgwwelldqep">
            <p className="text-[clamp(4.5rem,8vw,7.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-544" data-mp="3bsjlgfvfzgrg">
              22
            </p>
            <h3 className="mt-3 text-[clamp(1.25rem,2vw,1.75rem)] font-bold" data-id="element-545" data-mp="14yxxpnabfn70">
              Brands campaigned
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/75" data-id="element-546" data-mp="2uhwievho1ch6">
              Across different portfolios, lifecycle stages, and HCP needs.
            </p>
            <p className="mt-5 border-t border-white/20 pt-4 text-xs font-semibold uppercase tracking-[0.14em] text-white/65" data-id="element-547" data-mp="2pkwaef199znf">
              Continued delivery experience
            </p>
          </div>
        </motion.article>

        <motion.article initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42,
        duration: 0.55
      }} className="flex min-h-0 flex-col rounded-3xl border border-[var(--brand)]/10 bg-white p-[clamp(1.25rem,2vw,2rem)] shadow-sm lg:col-span-5" data-id="element-548" data-mp="3d8no9n2g2kqt">
          <div className="flex items-start justify-between gap-4" data-id="element-549" data-mp="3i8s55e79lhhk">
            <div className="flex items-center gap-3" data-id="element-550" data-mp="1ssizdqo3imn9">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-551" data-mp="1stc6wtuvpg6g">
                <Globe2Icon className="h-5 w-5" aria-hidden="true" data-id="element-552" data-mp="1nw28iopihu6h" />
              </span>
              <div data-id="element-553" data-mp="2djxwhu6yivcg">
                <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-554" data-mp="2hj6sl5pgz5b6">
                  5
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-555" data-mp="2mhrquzl5vewz">
                  Regions covered
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-556" data-mp="2wfxtxkiper3f">
              <p className="text-[clamp(2rem,3.2vw,3rem)] font-extrabold leading-none text-[var(--brand)]" data-id="element-557" data-mp="2wewgxai4nuj2">
                23
              </p>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--muted)]" data-id="element-558" data-mp="35a2ghrqt1gd5">
                Localizations
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-id="element-559" data-mp="3f7qmjdtv2aam">
            {REGIONS.map(region => <span key={region} className="rounded-full border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-3 py-1.5 text-xs font-bold text-[var(--brand)]" data-id="element-560" data-mp="3k6bdemn83hme">
                {region}
              </span>)}
          </div>

          <div className="mt-5 flex min-h-0 flex-1 flex-col rounded-2xl bg-[var(--surface)] p-4" data-id="element-561" data-mp="13jc1o6nlqftt">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[var(--fg)]" data-id="element-562" data-mp="yk8vujzss5ks">
              <MapPinIcon className="h-4 w-4 text-[var(--brand)]" aria-hidden="true" data-id="element-563" data-mp="tm7crz2dg69b" />
              Market footprint
            </div>
            <ul className="grid flex-1 grid-cols-3 gap-x-4 gap-y-1.5" data-id="element-564" data-mp="1r84sxpta3zi3">
              {MARKETS.map(market => <li key={market} className="flex items-center gap-1.5 text-[0.68rem] font-medium leading-tight text-[var(--muted)]" data-id="element-565" data-mp="1mbo04ib4wwyo">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-566" data-mp="1hha2e9902dqu" />
                  {market}
                </li>)}
            </ul>
          </div>
        </motion.article>
      </div>
    </InteriorSlide>;
}